<template>
  <div
    class="dark:bg-ontick-graphite-500 border-ontick-steel-300 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-lg border-2 pt-4 dark:border-0"
    :class="{
      'pb-4': data.status === 'STOPPED' || data.type === 'PORTFOLIO' || data.strategy_symbol === 'ANY',
    }"
  >
    <div class="pointer-events-none absolute inset-0 bg-gray-50 bg-opacity-75 dark:hidden"></div>
    <!-- CARD HEADER -->
    <header-component
      :data="data"
      :selectedVolume="selectedVolume"
      class="z-10 px-4"
      @start-modal="triggerStartModal"
      @edit-volume="fnEditVolume"
    />

    <transition
      enter-active-class="ease-out duration-300"
      enter-class="opacity-0  "
      enter-to-class="opacity-100 "
      leave-active-class="ease-in duration-200"
      leave-class="opacity-100"
      leave-to-class="opacity-0 "
    >
      <modal-edit-volume-component
        v-if="editVolume.open"
        :isOpen="editVolume.open"
        :data="editVolume.data"
        :mode="editVolume.mode"
        @close="editVolume = { mode: 'CREATE_SCHEDULE', open: false, data: null }"
        @reopen="reopenEditVolume"
      />
    </transition>

    <!-- PNL -->
    <template v-if="(data.type === 'PORTFOLIO' || data.strategy_symbol === 'ANY') && data.status === 'RUNNING'">
      <multi-symbol-pnl-component :item="data" class="z-10 px-4" />
    </template>
    <template v-else>
      <template v-if="data.status === 'RUNNING'">
        <pnl-component :data="data" class="z-10 px-4" />
      </template>

      <template v-else-if="data.status === 'STOPPED'">
        <template
          v-if="data.strategy_market === 'Equities' || (data.strategy_symbol === 'ANY' && data.type !== 'PORTFOLIO')"
        >
          <fin-input-component :investment="data" v-model="selectedVolume" class="z-10" />
          <!-- <card-edit-component
            class="px-4"
            :item="data"
            @seletedParam="
              setInvestmentVolume({
                investment_id: data.investment_id,
                volume: $event,
              })
            "
            :disabled="editDisabled"
          /> -->
        </template>
        <template v-else>
          <!-- VOLUME SLIDER -->
          <volume-slider-component
            :type="data.type"
            v-model="selectedVolume"
            :disabled="editDisabled"
            :manualInputEnabled="manualInputEnabled"
            :showManualInputButton="showManualInputButton"
            :marks="marks"
            :max="data.max_scale_volume"
            :min="data.min_volume"
            :volumeUnit="volumeUnit"
            @toggle-manual-input="manualInputEnabled = !manualInputEnabled"
            class="z-10 px-4"
          />

          <!-- GAIN/LOSS -->
          <div class="z-10 grid grid-cols-3 px-4">
            <dl>
              <template v-if="data.type !== 'PORTFOLIO'">
                <dt>Perda máx.</dt>
                <dd class="text-ontick-red-600">
                  {{ data.max_daily_loss | currency }}
                </dd>
              </template>
            </dl>
            <dl>
              <dt>Capital</dt>
              <dd class="text-ontick-steel-500 dark:text-gray-300">
                {{ (selectedVolume * (data.min_capital / data.volume_step)) | currency }}
              </dd>
            </dl>
            <dl>
              <template v-if="data.type !== 'PORTFOLIO'">
                <dt>Ganho máx.</dt>
                <dd class="text-ontick-green-600">
                  {{ data.max_daily_gain | currency }}
                </dd>
              </template>
            </dl>
          </div>
        </template>
      </template>
      <card-strategy-status-component :item="data" class="z-10" />
    </template>
    <transition-group name="mask" class="pointer-events-none absolute inset-0 z-10">
      <template v-if="data.masks_list">
        <div v-for="(mask, index) in data.masks_list" :key="index + _uid">
          <card-mask
            class="pointer-events-auto"
            v-if="data.masks_list.length > 0"
            :mask="mask"
            @close="closeMask(data.investment_id, mask)"
          />
        </div>
      </template>
    </transition-group>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import VolumeSliderComponent from "./stopped/volume-slider.component.vue";
import HeaderComponent from "./header.component.vue";
import PnlComponent from "./running/pnl.component.vue";
import CardStrategyStatusComponent from "../card-investment/card-strategy/card-strategy-status.component.vue";
import MultiSymbolPnlComponent from "../card-investment/card-portfolio/multi-symbol-pnl.component.vue";
import CardMask from "../card-investment/card-mask/card-mask.component.vue";
import FinInputComponent from "./stopped/fin-input.component.vue";
import CardEditComponent from "../card-investment/card-strategy/card-edit/card-edit.component.vue";
import ModalEditVolumeComponent from "../modal-edit-volume/modal-edit-volume.component.vue";

export default {
  components: {
    VolumeSliderComponent,
    HeaderComponent,
    PnlComponent,
    CardStrategyStatusComponent,
    MultiSymbolPnlComponent,
    CardMask,
    FinInputComponent,
    CardEditComponent,
    OntickModalComponent: () => import("../../../../../shared/components/basic/ontick-modal.component.vue"),
    ModalEditVolumeComponent,
  },

  props: {
    data: {
      type: Object,
    },
  },

  data() {
    return {
      manualInputEnabled: false,
      selectedVolume: this.data.curr_volume,
      editVolume: {
        mode: "CREATE_SCHEDULE",
        open: false,
        data: null,
      },
    };
  },

  computed: {
    ...mapGetters({
      investments: "investment/InvestmentsList",
    }),

    showManualInputButton() {
      return this.selectedVolume === this.data.max_scale_volume;
    },
    editDisabled() {
      return this.data.status === "RUNNING";
    },
    volumeUnit() {
      switch (this.data.volume_text) {
        case "contratos":
          if (this.selectedVolume > 1) {
            return "Contratos";
          } else {
            return "Contrato";
          }
        default:
          return "Contratos";
      }
    },
    hasBackgroundImage() {
      return this.data.type === "PORTFOLIO" || this.data.strategy_symbol === "ANY";
    },
    backgroundImage() {
      if (this.data.type === "PORTFOLIO") {
        return "/img/pie-chart-compressed.png";
      } else if (this.data.strategy_symbol === "ANY") {
        return "/img/arrows-compressed.png";
      }
    },
  },

  methods: {
    ...mapActions({
      getInvestments: "investment/getInvestments",
      removeMask: "investment/removeMask",
      setInvestmentVolume: "investment/setInvestmentVolume",
    }),
    closeMask(investment_id, mask) {
      this.removeMask({
        investment_id,
        mask,
      });
      if (mask.fixedMask) {
        this.$api
          .post(this.$endpoint.DISMISS_MASK, {
            investment_id: investment_id,
            notification_type: mask.type,
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    marks(val) {
      if (val % this.data.volume_step === 0) {
        return {
          label: val,
          labelStyle: {
            opacity: 1,
          },
        };
      }
      return false;
    },
    triggerStartModal() {
      // console.log("triggerStartModal");
    },
    dropdownOptionSelected() {},

    fnEditVolume(event) {
      if (Object.keys(event).includes("pending_schedule")) {
        this.editVolume.mode = "EDIT_SCHEDULE";
      } else {
        this.editVolume.mode = "CREATE_SCHEDULE";
      }
      this.editVolume.data = event;
      this.editVolume.open = true;
    },

    reopenEditVolume({ investment_id }) {
      console.log("Reopening edit volume modal", investment_id);
      const investment = this.investments.find((inv) => inv.investment_id === investment_id);
      if (investment) {
        console.log("Item found:", investment);
        this.editVolume.data = investment;
        this.editVolume.mode = "CREATE_SCHEDULE";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
dl {
  @apply flex flex-col items-center gap-1 first:items-start last:items-end;
  dt {
    @apply text-xs text-gray-500 dark:text-gray-400;
  }
  dd {
    @apply text-center text-sm font-semibold;
  }
}
</style>
